import React, { useState, useLayoutEffect, useRef, useEffect } from 'react'
import { MaxWidthProps } from 'styled-system'
import styled, { css } from 'styled-components'

import Text, { TextProps } from 'components/Text'
import BodyPortal from '../BodyPortal'
import Box from 'components/Box'

import useDebounceValue from 'hooks/use-debounce-value'

import { Z_INDEX } from 'utils/constants'
import { WidthProps } from 'types/styled'

export type PositionType = 'top' | 'bottom' | 'left' | 'right' | 'bottomRight' | 'topRight' | 'topLeft' | 'bottomLeft'
export type TooltipVariant = 'dark' | 'light'
export type TooltipProps = {
  inline?: boolean
  badge?: boolean
  children?: React.ReactNode
  position?: PositionType
  textTransform?: 'capitalize' | 'lowercase' | 'none' | 'uppercase'
  title: string | React.ReactNode
  textWidth?: WidthProps | string
  textAlign?: 'left' | 'center' | 'right'
  isVisible?: boolean
  contentMaxWidth?: MaxWidthProps['maxWidth']
  zIndex?: number
  maxHeight?: number
  isScrollable?: boolean
  /** hover (default) or click to toggle visibility */
  trigger?: 'hover' | 'click'
  /** Controlled open state — use with trigger="click" */
  open?: boolean
  onOpenChange?: (open: boolean) => void
  /** dark = default popover; light = white card (no arrow) */
  variant?: TooltipVariant
}

type Props = Omit<TextProps, 'children' | 'variant'> & TooltipProps

type _TooltipProps = {
  childrenWidth: number
  childrenHeight: number
  visible: boolean
  zIndex?: number
  onMouseEnter?: React.MouseEventHandler<HTMLDivElement>
  onMouseLeave?: React.MouseEventHandler<HTMLDivElement>
} & Props

const popupRoot = document.getElementById('popup-root')

const Tooltip = ({
  children,
  inline = false,
  isVisible = true,
  textAlign = 'center',
  contentMaxWidth,
  zIndex,
  isScrollable = false,
  maxHeight,
  badge = false,
  textTransform = 'none',
  position = 'top',
  trigger = 'hover',
  open: openProp,
  onOpenChange,
  variant = 'dark',
  ...props
}: Props) => {
  const tooltipContentRef = useRef<HTMLDivElement>(null)
  const ref = useRef<HTMLDivElement>(null)
  const [childrenSize, setChildrenSize] = useState({ width: 0, height: 0 })
  const isClickTrigger = trigger === 'click'
  const isControlledOpen = openProp !== undefined
  const [clickOpen, setClickOpen] = useState(false)
  const clickVisible = isControlledOpen ? openProp : clickOpen
  // A scrollable tooltip needs this 200ms delay to allow the user to move the cursor to the tooltip for scrolling.
  const [hoverVisible, setHoverVisible] = useDebounceValue<boolean>(false, isScrollable ? 200 : 0)
  const [isPortaled, setIsPortaled] = useState(false)
  const [adjustedPosition, setAdjustedPosition] = useState<PositionType>(position)

  const visible = isClickTrigger ? clickVisible && isVisible : hoverVisible

  const setClickVisible = (next: boolean) => {
    if (isControlledOpen) onOpenChange?.(next)
    else setClickOpen(next)
  }

  const handleMouseEnter = () => {
    if (!isPortaled) setIsPortaled(true)
    setHoverVisible(true && isVisible)
  }

  const handleMouseOut = () => {
    setHoverVisible(false)
  }

  const handleChildClick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation()
    if (!isPortaled) setIsPortaled(true)
    setClickVisible(!clickVisible)
  }

  const handleTooltipMouseEnter = () => {
    // Only for scrollable tooltip, needs to set Visible true to keep the tooltip open for scrolling
    if (isScrollable) setHoverVisible(true)
  }
  const handleTooltipMouseLeave = () => {
    if (isScrollable) setHoverVisible(false)
  }

  useEffect(() => {
    if (!isClickTrigger || !visible) return

    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node
      if (ref.current?.contains(target)) return
      if (tooltipContentRef.current?.contains(target)) return
      setClickVisible(false)
    }
    const handleScroll = () => setClickVisible(false)

    document.addEventListener('mousedown', handleClickOutside)
    window.addEventListener('scroll', handleScroll, true)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      window.removeEventListener('scroll', handleScroll, true)
    }
  }, [isClickTrigger, visible, isControlledOpen, onOpenChange])

  useEffect(() => {
    if (!visible) setAdjustedPosition(position)
  }, [visible, position])

  useLayoutEffect(() => {
    const currTarget = ref.current
    const containerRef = tooltipContentRef.current

    if (currTarget && containerRef) {
      const rect = currTarget.getBoundingClientRect()

      setChildrenSize({ width: rect.width, height: rect.height })

      containerRef.style.top = rect.y + rect.height / 2 + window.scrollY + 'px'
      containerRef.style.left = rect.x + rect.width / 2 + window.scrollX + 'px'

      if (visible) {
        const tooltipRect = containerRef.getBoundingClientRect()
        const viewportHeight = window.innerHeight
        const viewportWidth = window.innerWidth
        const spaceAbove = rect.top
        const spaceBelow = viewportHeight - rect.bottom
        const tooltipHeight = tooltipRect.height

        let calculatedPosition = position

        if (['top', 'topLeft', 'topRight'].includes(position)) {
          if (spaceAbove < tooltipHeight + 20 && spaceBelow > spaceAbove) {
            calculatedPosition = position.replace('top', 'bottom') as PositionType
          }
        } else if (['bottom', 'bottomLeft', 'bottomRight'].includes(position)) {
          if (spaceBelow < tooltipHeight + 20 && spaceAbove > spaceBelow) {
            calculatedPosition = position.replace('bottom', 'top') as PositionType
          }
        }

        if (['bottomLeft', 'topLeft'].includes(calculatedPosition)) {
          if (tooltipRect.right > viewportWidth) {
            calculatedPosition = calculatedPosition.replace('Left', 'Right') as PositionType
          }
        } else if (['bottomRight', 'topRight'].includes(calculatedPosition)) {
          if (tooltipRect.left < 0) {
            calculatedPosition = calculatedPosition.replace('Right', 'Left') as PositionType
          }
        }

        setAdjustedPosition(calculatedPosition)
      }
    }
  }, [tooltipContentRef, visible, ref, position])

  return (
    <>
      <BodyPortal rootNode={popupRoot}>
        {isPortaled && (
          <_Tooltip
            {...props}
            badge={badge}
            variant={variant}
            textTransform={textTransform}
            position={adjustedPosition}
            ref={tooltipContentRef}
            childrenHeight={childrenSize.height}
            childrenWidth={childrenSize.width}
            visible={visible}
            textAlign={textAlign}
            maxWidth={contentMaxWidth}
            maxHeight={maxHeight}
            zIndex={zIndex}
            onMouseEnter={handleTooltipMouseEnter}
            onMouseLeave={handleTooltipMouseLeave}
          />
        )}
      </BodyPortal>
      <ChildContainer
        inline={inline}
        ref={ref}
        onMouseOver={isClickTrigger ? undefined : handleMouseEnter}
        onMouseOut={isClickTrigger ? undefined : handleMouseOut}
        onClick={isClickTrigger ? handleChildClick : undefined}
      >
        {children}
      </ChildContainer>
    </>
  )
}

const _Tooltip = React.forwardRef<HTMLDivElement, _TooltipProps>((props, ref) => {
  const {
    badge = false,
    variant = 'dark',
    position = 'top',
    textTransform = 'none',
    title,
    textWidth = 'max-content',
    visible,
    childrenHeight,
    childrenWidth,
    textAlign,
    wordBreak,
    whiteSpace,
    overflowWrap,
    maxWidth,
    maxHeight,
    zIndex,
    onMouseEnter,
    onMouseLeave,
  } = props

  const isLight = variant === 'light' && !badge

  return (
    <Container>
      {visible && (
        <ToolTipContent
          badge={badge ?? false}
          variant={variant}
          position={position ?? 'top'}
          ref={ref}
          textTransform={textTransform ?? 'none'}
          childrenHeight={childrenHeight}
          childrenWidth={childrenWidth}
          maxWidth={maxWidth}
          zIndex={zIndex}
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
        >
          {!isLight && <Arrow badge={badge ?? false} variant={variant} position={position ?? 'top'} />}
          <Box maxHeight={maxHeight} overflow={'auto'} padding={isLight ? '12px 16px' : '8px'}>
            <Text
              color={isLight ? 'defaultText' : 'gray2'}
              family="roboto"
              fontWeight={badge ? 'bold' : isLight ? 'normal' : 'medium'}
              fontSize={badge ? 11 : 13}
              lineHeight={isLight ? '1.45' : '16px'}
              maxWidth={textWidth}
              textAlign={textAlign}
              textTransform={textTransform ?? 'none'}
              wordBreak={wordBreak || 'normal'}
              whiteSpace={whiteSpace || 'normal'}
              overflowWrap={overflowWrap || 'break-word'}
              isCapitalizedFirstLetter={!isLight && typeof title === 'string'}
            >
              {title}
            </Text>
          </Box>
        </ToolTipContent>
      )}
    </Container>
  )
})

export default Tooltip

const Container = styled.div`
  position: absolute;
  height: 0;
`

const ChildContainer = styled.div<{ inline: boolean }>`
  ${(p) => p.inline && `display: inline-block`}
`

const Arrow = styled.span<{ badge: boolean; variant: TooltipVariant; position: string }>`
  --border: 10px solid;
  --color: ${(p) => {
    if (p.badge) return p.theme.colors.orange
    if (p.variant === 'light') return p.theme.colors.white
    return p.theme.colors.defaultText
  }};

  position: absolute;
  width: 0;
  height: 0;
  ${(p) =>
    p.position === 'top' &&
    css`
      bottom: -5px;
      left: calc(50% - 10px);
      border-right: var(--border) transparent;
      border-top: var(--border) var(--color);
      border-left: var(--border) transparent;
    `}
  ${(p) =>
    p.position === 'bottom' &&
    css`
      top: -5px;
      left: calc(50% - 10px);
      border-right: var(--border) transparent;
      border-bottom: var(--border) var(--color);
      border-left: var(--border) transparent;
    `}
  ${(p) =>
    p.position === 'right' &&
    css`
      top: calc(50% - 10px);
      border-top: var(--border) transparent;
      border-bottom: var(--border) transparent;
      left: -5px;
      border-right: var(--border) var(--color);
    `}
  ${(p) =>
    p.position === 'left' &&
    css`
      top: calc(50% - 10px);
      border-top: var(--border) transparent;
      border-bottom: var(--border) transparent;
      right: -5px;
      border-left: var(--border) var(--color);
    `}
  ${(p) =>
    p.position === 'bottomRight' &&
    css`
      top: -5px;
      left: calc(100% - 30px);
      border-right: var(--border) transparent;
      border-bottom: var(--border) var(--color);
      border-left: var(--border) transparent;
    `}
  ${(p) =>
    p.position === 'bottomLeft' &&
    css`
      top: -5px;
      left: 0;
      border-right: var(--border) transparent;
      border-bottom: var(--border) var(--color);
      border-left: var(--border) transparent;
    `}
  ${(p) =>
    p.position === 'topRight' &&
    css`
      bottom: -5px;
      left: calc(100% - 30px);
      border-right: var(--border) transparent;
      border-top: var(--border) var(--color);
      border-left: var(--border) transparent;
    `}
  ${(p) =>
    p.position === 'topLeft' &&
    css`
      bottom: -5px;
      left: 0;
      border-right: var(--border) transparent;
      border-top: var(--border) var(--color);
      border-left: var(--border) transparent;
    `}
`

const ToolTipContent = styled.div<{
  badge: boolean
  variant: TooltipVariant
  position: string
  textTransform: string
  childrenHeight: number
  childrenWidth: number
  maxWidth?: Props['contentMaxWidth']
  zIndex?: number
}>`
  --children-height: ${(p) => p.childrenHeight}px;
  --tooltip-arrow-size: ${(p) => (p.variant === 'light' && !p.badge ? '0px' : '10px')};
  --tooltip-postion-top-translate-y: calc(-100% - (var(--children-height) / 2) - (var(--tooltip-arrow-size) / 2) - 1px);
  --tooltip-postion-bottom-translate-y: calc((var(--children-height) / 2) + (var(--tooltip-arrow-size) / 2) + 1px);
  --tooltip-corner-left-translate-x: calc(-${(p) => p.childrenWidth / 2}px - 1px);
  --tooltip-corner-right-translate-x: calc(-100% + ${(p) => p.childrenWidth / 2}px + 1px);

  animation: fadeIn ease-in-out 0.65s;
  background-color: ${(p) => {
    if (p.badge) return p.theme.colors.orange
    if (p.variant === 'light') return p.theme.colors.white
    return p.theme.colors.defaultText
  }};
  border: ${(p) => (p.variant === 'light' && !p.badge ? `1px solid ${p.theme.colors.gray3}` : 'none')};
  border-radius: ${(p) => (p.variant === 'light' && !p.badge ? '8px' : '3px')};
  box-shadow: ${(p) =>
    p.variant === 'light' && !p.badge ? '0 8px 24px rgba(0, 0, 0, 0.12)' : '0 0 0.3rem rgba(255, 255, 255, 0.16)'};
  color: ${(p) => (p.variant === 'light' && !p.badge ? p.theme.colors.defaultText : p.theme.colors.white)};
  cursor: default;
  max-width: ${(p) => (p.maxWidth ? p.maxWidth : '275px')};
  word-break: ${(p) => (p.variant === 'light' && !p.badge ? 'normal' : 'break-all')};
  position: relative;
  z-index: ${(p) => p.zIndex || Z_INDEX.tooltip};
  box-sizing: border-box;

  ${(p) =>
    p.variant === 'light' &&
    !p.badge &&
    css`
      a {
        color: ${p.theme.colors.blue1};
      }
    `}

  *::-webkit-scrollbar-thumb {
    background-color: ${({ theme }) => theme.colors.gray5} !important;
  }

  *::-webkit-scrollbar {
    width: 0.9rem;
  }

  ${(p) =>
    p.position === 'top' &&
    css`
      transform: translate(-50%, var(--tooltip-postion-top-translate-y));
    `}
  ${(p) =>
    p.position === 'bottom' &&
    css`
      transform: translate(-50%, var(--tooltip-postion-bottom-translate-y));
    `}
  ${(p) =>
    p.position === 'left' &&
    css`
      transform: translate(calc(-100% - ${p.childrenWidth / 2}px - (var(--tooltip-arrow-size) / 2) - 1px), -50%);
    `}
  ${(p) =>
    p.position === 'right' &&
    css`
      transform: translate(calc(${p.childrenWidth / 2}px + (var(--tooltip-arrow-size) / 2) + 1px), -50%);
    `}
  ${(p) =>
    p.position === 'bottomRight' &&
    css`
      transform: translate(var(--tooltip-corner-right-translate-x), var(--tooltip-postion-bottom-translate-y));
    `}
  ${(p) =>
    p.position === 'bottomLeft' &&
    css`
      transform: translate(var(--tooltip-corner-left-translate-x), var(--tooltip-postion-bottom-translate-y));
    `}
  ${(p) =>
    p.position === 'topRight' &&
    css`
      transform: translate(var(--tooltip-corner-right-translate-x), var(--tooltip-postion-top-translate-y));
    `}
  ${(p) =>
    p.position === 'topLeft' &&
    css`
      transform: translate(var(--tooltip-corner-left-translate-x), var(--tooltip-postion-top-translate-y));
    `}
  ${(p) =>
    p.badge &&
    css`
      left: 25px;
      padding: 3px;
      top: 4px;
      width: auto;
    `}
`
